import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import SafetyScoreWidget from "@/components/SafetyScoreWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Users, 
  BookOpen, 
  DollarSign, 
  TrendingUp,
  CheckCircle2,
  Clock,
  ArrowRight,
  Mail,
  Bell
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    fetchStats();
    seedData();
  }, []);

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore if already seeded
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/admin`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async (type) => {
    setSendingReminders(true);
    try {
      const endpoint = type === 'training' 
        ? `${API}/reminders/send-training`
        : `${API}/reminders/send-certificate-expiry`;
      const response = await axios.post(endpoint, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const statCards = [
    { 
      title: "Total Companies", 
      value: stats?.total_companies || 0, 
      icon: Building2, 
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    { 
      title: "Total Employees", 
      value: stats?.total_employees || 0, 
      icon: Users, 
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    },
    { 
      title: "Active Courses", 
      value: stats?.total_courses || 0, 
      icon: BookOpen, 
      color: "text-violet-600",
      bgColor: "bg-violet-100"
    },
    { 
      title: "Total Revenue", 
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: "text-amber-600",
      bgColor: "bg-amber-100"
    }
  ];

  const completionData = [
    { name: "Completed", value: stats?.completed_assignments || 0 },
    { name: "In Progress", value: (stats?.total_assignments || 0) - (stats?.completed_assignments || 0) }
  ];

  const COLORS = ['#10B981', '#F59E0B'];

  // Mock monthly data for chart
  const monthlyData = [
    { month: 'Jan', revenue: 12000, companies: 45 },
    { month: 'Feb', revenue: 15000, companies: 52 },
    { month: 'Mar', revenue: 18000, companies: 61 },
    { month: 'Apr', revenue: 22000, companies: 78 },
    { month: 'May', revenue: 28000, companies: 95 },
    { month: 'Jun', revenue: stats?.total_revenue || 32000, companies: stats?.total_companies || 120 }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here's what's happening with your platform today.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => sendReminders('training')}
              disabled={sendingReminders}
              data-testid="send-training-reminders-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Send Training Reminders
            </Button>
            <Button 
              variant="outline" 
              onClick={() => sendReminders('cert')}
              disabled={sendingReminders}
              data-testid="send-cert-reminders-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Cert Expiry Alerts
            </Button>
          </div>
        </div>

        {/* Safety Score Widget */}
        <SafetyScoreWidget />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Card key={i} className="stat-card" data-testid={`stat-card-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="font-heading text-2xl font-bold text-primary">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Revenue Chart */}
          <Card className="lg:col-span-8">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Revenue Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="revenue" fill="#F97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Completion Rate Chart */}
          <Card className="lg:col-span-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Training Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={completionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {completionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-success"></div>
                  <span className="text-sm text-muted-foreground">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-warning"></div>
                  <span className="text-sm text-muted-foreground">In Progress</span>
                </div>
              </div>
              <div className="text-center mt-4">
                <p className="text-3xl font-heading font-bold text-primary">{stats?.completion_rate || 0}%</p>
                <p className="text-sm text-muted-foreground">Overall Completion Rate</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Companies & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Recent Companies */}
          <Card className="lg:col-span-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Companies</CardTitle>
              <Link to="/companies">
                <Button variant="ghost" size="sm" data-testid="view-all-companies-btn">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_companies?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_companies.map((company, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center text-white font-semibold">
                          {company.name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="font-medium text-primary">{company.name}</p>
                          <p className="text-sm text-muted-foreground">{company.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`badge-${company.subscription_status === 'active' ? 'success' : 'warning'}`}>
                          {company.subscription_plan}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-1">
                          {company.employee_count} employees
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No companies yet</p>
                  <Link to="/companies">
                    <Button className="mt-4" data-testid="add-first-company-btn">
                      Add First Company
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="lg:col-span-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/companies" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-add-company-btn">
                  <Building2 className="w-4 h-4 mr-3" />
                  Add New Company
                </Button>
              </Link>
              <Link to="/courses" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-manage-courses-btn">
                  <BookOpen className="w-4 h-4 mr-3" />
                  Manage Courses
                </Button>
              </Link>
              <Link to="/marketing" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-ai-marketing-btn">
                  <TrendingUp className="w-4 h-4 mr-3" />
                  AI Marketing Studio
                </Button>
              </Link>
              <Link to="/billing" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-billing-btn">
                  <DollarSign className="w-4 h-4 mr-3" />
                  View Billing
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
